import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getPublicStats,
  getPublicMembers,
  getPublicTeams,
  getPublicProjects,
  getExportUrl,
  getSuperteams,
  getCountries,
} from '../utils/api';
import type {
  PublicMember,
  PublicTeam,
  PublicProject,
  Superteam,
  Country,
} from '../types';

type TabType = 'members' | 'teams' | 'projects';

interface CatalogueProps {
  onClose: () => void;
}

const ITEMS_PER_PAGE = 25;

export default function Catalogue({ onClose }: CatalogueProps) {
  const [activeTab, setActiveTab] = useState<TabType>('members');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [superteamFilter, setSuperteamFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, superteamFilter, countryFilter]);

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['publicStats'],
    queryFn: getPublicStats,
    staleTime: 60000,
  });

  // Fetch superteams for filter
  const { data: superteamsData } = useQuery({
    queryKey: ['superteams'],
    queryFn: getSuperteams,
    staleTime: 300000,
  });

  // Fetch countries for filter
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
    staleTime: 300000,
  });

  // Fetch members
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['publicMembers', debouncedSearch, superteamFilter, countryFilter, currentPage],
    queryFn: () => getPublicMembers({
      search: debouncedSearch || undefined,
      superteamId: superteamFilter || undefined,
      countryCode: countryFilter || undefined,
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    }),
    enabled: activeTab === 'members',
  });

  // Fetch teams
  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['publicTeams', debouncedSearch, currentPage],
    queryFn: () => getPublicTeams({
      search: debouncedSearch || undefined,
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    }),
    enabled: activeTab === 'teams',
  });

  // Fetch projects
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['publicProjects', debouncedSearch, currentPage],
    queryFn: () => getPublicProjects({
      search: debouncedSearch || undefined,
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    }),
    enabled: activeTab === 'projects',
  });

  const stats = statsData?.data;
  const superteams = superteamsData?.superteams || [];
  const countries = countriesData?.countries || [];
  const members = membersData?.data?.members || [];
  const teams = teamsData?.data?.teams || [];
  const projects = projectsData?.data?.projects || [];

  const totalCount = useMemo(() => {
    if (activeTab === 'members') return membersData?.data?.totalCount || 0;
    if (activeTab === 'teams') return teamsData?.data?.totalCount || 0;
    if (activeTab === 'projects') return projectsData?.data?.totalCount || 0;
    return 0;
  }, [activeTab, membersData, teamsData, projectsData]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const isLoading = activeTab === 'members' ? membersLoading : activeTab === 'teams' ? teamsLoading : projectsLoading;

  const handleDownload = useCallback(() => {
    const url = getExportUrl(activeTab, {
      search: debouncedSearch || undefined,
      superteamId: superteamFilter || undefined,
      countryCode: countryFilter || undefined,
    });
    window.open(url, '_blank');
  }, [activeTab, debouncedSearch, superteamFilter, countryFilter]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Styles
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  };

  const containerStyle: CSSProperties = {
    background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.98) 0%, rgba(25, 25, 55, 0.98) 100%)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    width: '100%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  };

  const headerStyle: CSSProperties = {
    padding: '24px 28px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    flexWrap: 'wrap',
  };

  const statsGridStyle: CSSProperties = {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  };

  const statBoxStyle: CSSProperties = {
    textAlign: 'center',
  };

  const statValueStyle: CSSProperties = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#fff',
    lineHeight: 1,
  };

  const statLabelStyle: CSSProperties = {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const closeButtonStyle: CSSProperties = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '10px',
    padding: '10px',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.2s ease',
  };

  const tabsStyle: CSSProperties = {
    display: 'flex',
    gap: '8px',
    padding: '16px 28px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const tabStyle = (isActive: boolean): CSSProperties => ({
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: isActive ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
  });

  const filtersStyle: CSSProperties = {
    display: 'flex',
    gap: '12px',
    padding: '16px 28px',
    flexWrap: 'wrap',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  };

  const inputStyle: CSSProperties = {
    padding: '10px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    minWidth: '200px',
  };

  const selectStyle: CSSProperties = {
    ...inputStyle,
    minWidth: '160px',
    cursor: 'pointer',
  };

  const downloadButtonStyle: CSSProperties = {
    marginLeft: 'auto',
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  };

  const tableContainerStyle: CSSProperties = {
    flex: 1,
    overflow: 'auto',
    padding: '0 28px',
  };

  const tableStyle: CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  };

  const thStyle: CSSProperties = {
    textAlign: 'left',
    padding: '14px 12px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'sticky',
    top: 0,
    background: 'rgba(15, 15, 35, 0.98)',
  };

  const tdStyle: CSSProperties = {
    padding: '14px 12px',
    color: 'rgba(255, 255, 255, 0.85)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    verticalAlign: 'middle',
  };

  const avatarStyle: CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
  };

  const badgeStyle = (isVerified: boolean): CSSProperties => ({
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    background: isVerified ? 'rgba(16, 185, 129, 0.2)' : 'rgba(156, 163, 175, 0.2)',
    color: isVerified ? '#10b981' : 'rgba(255, 255, 255, 0.5)',
  });

  const linkStyle: CSSProperties = {
    color: '#818cf8',
    textDecoration: 'none',
  };

  const paginationStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 28px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const pageButtonStyle = (disabled: boolean): CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(99, 102, 241, 0.3)',
    color: disabled ? 'rgba(255, 255, 255, 0.3)' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  });

  const emptyStyle: CSSProperties = {
    padding: '60px 20px',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.5)',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={containerStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header with Stats */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '24px', fontWeight: '700' }}>
              Open Data Catalogue
            </h2>
            <p style={{ margin: '8px 0 0', color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
              Explore and download public data from the Superwhere ecosystem
            </p>
          </div>
          {stats && (
            <div style={statsGridStyle}>
              <div style={statBoxStyle}>
                <div style={statValueStyle}>{stats.totalMembers.toLocaleString()}</div>
                <div style={statLabelStyle}>Members</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValueStyle}>{stats.verifiedMembers.toLocaleString()}</div>
                <div style={statLabelStyle}>Verified</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValueStyle}>{stats.totalTeams.toLocaleString()}</div>
                <div style={statLabelStyle}>Teams</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValueStyle}>{stats.totalProjects.toLocaleString()}</div>
                <div style={statLabelStyle}>Projects</div>
              </div>
            </div>
          )}
          <button style={closeButtonStyle} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div style={tabsStyle}>
          <button style={tabStyle(activeTab === 'members')} onClick={() => setActiveTab('members')}>
            Members
          </button>
          <button style={tabStyle(activeTab === 'teams')} onClick={() => setActiveTab('teams')}>
            Teams
          </button>
          <button style={tabStyle(activeTab === 'projects')} onClick={() => setActiveTab('projects')}>
            Projects
          </button>
        </div>

        {/* Filters */}
        <div style={filtersStyle}>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle}
          />
          {activeTab === 'members' && (
            <>
              <select
                value={superteamFilter}
                onChange={(e) => setSuperteamFilter(e.target.value)}
                style={selectStyle}
              >
                <option value="">All Superteams</option>
                {superteams.map((st: Superteam) => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                style={selectStyle}
              >
                <option value="">All Countries</option>
                {countries.map((c: Country) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </>
          )}
          <button style={downloadButtonStyle} onClick={handleDownload}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Download CSV
          </button>
        </div>

        {/* Table */}
        <div style={tableContainerStyle}>
          {isLoading ? (
            <div style={emptyStyle}>Loading...</div>
          ) : (
            <>
              {activeTab === 'members' && (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>User</th>
                      <th style={thStyle}>Bio</th>
                      <th style={thStyle}>Superteam</th>
                      <th style={thStyle}>Country</th>
                      <th style={thStyle}>Skills</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                          No members found
                        </td>
                      </tr>
                    ) : (
                      members.map((member: PublicMember) => (
                        <tr key={member.id}>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <img
                                src={member.xAvatarUrl || '/default-avatar.png'}
                                alt=""
                                style={avatarStyle}
                              />
                              <div>
                                <div style={{ fontWeight: '600' }}>{member.xDisplayName}</div>
                                <a
                                  href={`https://x.com/${member.xUsername}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...linkStyle, fontSize: '12px' }}
                                >
                                  @{member.xUsername}
                                </a>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {member.bio || '-'}
                          </td>
                          <td style={tdStyle}>{member.superteamName || '-'}</td>
                          <td style={tdStyle}>{member.countryName || '-'}</td>
                          <td style={{ ...tdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {member.skills?.join(', ') || '-'}
                          </td>
                          <td style={tdStyle}>
                            <span style={badgeStyle(member.isVerified)}>
                              {member.isVerified ? 'Verified' : 'Unverified'}
                            </span>
                          </td>
                          <td style={tdStyle}>{formatDate(member.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'teams' && (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Team</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Owner</th>
                      <th style={thStyle}>Members</th>
                      <th style={thStyle}>Projects</th>
                      <th style={thStyle}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                          No teams found
                        </td>
                      </tr>
                    ) : (
                      teams.map((team: PublicTeam) => (
                        <tr key={team.id}>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {team.logoUrl ? (
                                <img src={team.logoUrl} alt="" style={{ ...avatarStyle, borderRadius: '8px' }} />
                              ) : (
                                <div style={{
                                  ...avatarStyle,
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  fontSize: '14px',
                                }}>
                                  {team.name.charAt(0)}
                                </div>
                              )}
                              <span style={{ fontWeight: '600' }}>{team.name}</span>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {team.description || '-'}
                          </td>
                          <td style={tdStyle}>
                            <a
                              href={`https://x.com/${team.ownerUsername}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={linkStyle}
                            >
                              @{team.ownerUsername}
                            </a>
                          </td>
                          <td style={tdStyle}>{team.memberCount}</td>
                          <td style={tdStyle}>{team.projectCount}</td>
                          <td style={tdStyle}>{formatDate(team.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'projects' && (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Project</th>
                      <th style={thStyle}>Tagline</th>
                      <th style={thStyle}>Team</th>
                      <th style={thStyle}>Links</th>
                      <th style={thStyle}>Media</th>
                      <th style={thStyle}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                          No projects found
                        </td>
                      </tr>
                    ) : (
                      projects.map((project: PublicProject) => (
                        <tr key={project.id}>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: '600' }}>{project.name}</span>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.tagline || '-'}
                          </td>
                          <td style={tdStyle}>{project.teamName}</td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {project.website && (
                                <a href={project.website} target="_blank" rel="noopener noreferrer" style={linkStyle} title="Website">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                  </svg>
                                </a>
                              )}
                              {project.github && (
                                <a href={project.github} target="_blank" rel="noopener noreferrer" style={linkStyle} title="GitHub">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
                                  </svg>
                                </a>
                              )}
                              {project.twitter && (
                                <a href={project.twitter} target="_blank" rel="noopener noreferrer" style={linkStyle} title="Twitter/X">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                  </svg>
                                </a>
                              )}
                              {project.discord && (
                                <a href={project.discord} target="_blank" rel="noopener noreferrer" style={linkStyle} title="Discord">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                  </svg>
                                </a>
                              )}
                              {!project.website && !project.github && !project.twitter && !project.discord && '-'}
                            </div>
                          </td>
                          <td style={tdStyle}>{project.mediaCount}</td>
                          <td style={tdStyle}>{formatDate(project.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        <div style={paginationStyle}>
          <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount.toLocaleString()} results
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              style={pageButtonStyle(currentPage === 1)}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', padding: '0 8px' }}>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              style={pageButtonStyle(currentPage >= totalPages)}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
